import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { NotificationStreamEvent } from './api.js';
import { env } from './env.js';
import { showNotificationToast } from './notification-toast.js';

// SSE subscriber + toast renderer. Lives at the dashboard root so a
// single connection per session services every page. Reconnects on
// disconnect with a capped exponential backoff (1s → 30s).
//
// Why a custom hook + not in a route loader: route loaders fire on
// navigation. We want the stream to stay open across navigations and
// even when the founder is on a route that doesn't itself read
// notifications. The hook subscribes in useEffect from a component
// mounted in main.tsx (NotificationStreamMount).

export interface UseNotificationStreamOptions {
  /** When false, the hook is a no-op. Used to gate on auth — pass
   *  `false` until `meQuery` resolves to a user. */
  enabled: boolean;
}

export function useNotificationStream({ enabled }: UseNotificationStreamOptions): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let source: EventSource | null = null;
    let backoffMs = 1_000;
    const maxBackoffMs = 30_000;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (cancelled) return;
      // EventSource only supports same-origin cookies via withCredentials.
      // The api lives at env.API_URL which in prod is on a sibling domain;
      // withCredentials passes the better-auth session cookie either way.
      source = new EventSource(`${env.API_URL}/api/notifications/stream`, {
        withCredentials: true,
      });

      source.addEventListener('ready', () => {
        // Reset backoff once the server acknowledges the subscription.
        backoffMs = 1_000;
      });

      source.addEventListener('notification', (e) => {
        try {
          const payload = JSON.parse((e as MessageEvent).data) as NotificationStreamEvent;
          handleIncomingNotification(payload, queryClient);
        } catch (err) {
          // Swallow parse errors — the next emit will be sound and we
          // don't want one bad event to tear down the connection.
          console.error('[notifications-stream] parse failed', err);
        }
      });

      source.addEventListener('ping', () => {
        // Heartbeat from the server. No-op; just keeps the connection warm.
      });

      source.addEventListener('error', () => {
        // EventSource auto-reconnects on transient errors, but we close
        // + retry with explicit backoff so the dev console doesn't fire
        // 1s-pinging forever when the api is down. The browser surfaces
        // the close as `readyState = CLOSED`.
        source?.close();
        source = null;
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, backoffMs);
        backoffMs = Math.min(backoffMs * 2, maxBackoffMs);
      });
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      source?.close();
    };
    // queryClient is stable across renders (returned by useQueryClient
    // from the provider) — including it pacifies the lint rule without
    // forcing a re-subscribe.
  }, [enabled, queryClient]);
}

/** Pure side-effect helper — fires the toast + invalidates caches. Split
 *  out so unit tests can drive it directly without spinning up an
 *  EventSource. */
export function handleIncomingNotification(
  payload: NotificationStreamEvent,
  queryClient: QueryClient,
): void {
  showNotificationToast(payload);
  // Bump every cache the inbox + bell read from. Invalidate is cheap;
  // refetches only fire for queries with active observers.
  void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
  void queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
}
